import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { api } from '@/shared/api/client'
import { qk } from '@/shared/api/query-keys'
import { errorMessage } from '@/shared/components/data'
import { useT } from '@/shared/i18n'
import type { Uuid } from '@/shared/types/domain'

import type { BriefPublico, Contacto, Invitacion, InvitacionEmitida, Respuesta } from './types'

/* ------------------------------------------------------------- público -- */

/**
 * El formulario que ve quien abre el link. **Sin credenciales.**
 *
 * `retry: false` a propósito: un token inválido devuelve 404 y reintentarlo
 * tres veces sólo retrasa el cartel de "este link no sirve". Y peor, en un
 * endpoint público con límite de tasa, gasta intentos de alguien que ya perdió.
 */
export function useBriefPublico(token: string) {
  return useQuery({
    queryKey: ['onboarding', 'brief', token],
    queryFn: () => api.get<BriefPublico>(`onboarding/token/${token}`),
    retry: false,
    refetchOnWindowFocus: false,
  })
}

export interface EnvioDelBrief {
  answers: Record<string, Respuesta>
  contacto: Contacto
}

export function useEnviarBrief(token: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (envio: EnvioDelBrief) =>
      api.post<void>(`onboarding/token/${token}`, {
        answers: envio.answers,
        contacto: {
          name: envio.contacto.name,
          email: envio.contacto.email,
          // El backend acepta `null`, no cadena vacía, para lo opcional.
          role: envio.contacto.role.trim() || null,
          phone: envio.contacto.phone.trim() || null,
        },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['onboarding', 'brief', token] })
    },
  })
}

/* ------------------------------------------------------------- interno -- */

export function useInvitaciones(projectId: Uuid) {
  return useQuery({
    queryKey: qk.projects.onboarding(projectId),
    queryFn: () => api.get<Invitacion[]>(`projects/${projectId}/onboarding`),
  })
}

export interface EmitirInput {
  sent_to_email: string
  sent_to_name?: string | null
  expires_in_days?: number
}

export function useEmitirInvitacion(projectId: Uuid) {
  const client = useQueryClient()
  const t = useT()

  return useMutation({
    mutationFn: (data: EmitirInput) =>
      api.post<InvitacionEmitida>(`projects/${projectId}/onboarding`, { ...data }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.projects.onboarding(projectId) })
    },
    onError: (error) => {
      toast.error(errorMessage(error, t('onboarding.error.emitir')))
    },
  })
}

export function useAceptarInvitacion(projectId: Uuid) {
  const client = useQueryClient()
  const t = useT()

  return useMutation({
    mutationFn: ({ id, createContact }: { id: Uuid; createContact: boolean }) =>
      api.post<Invitacion>(`onboarding/${id}/accept`, { create_contact: createContact }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.projects.onboarding(projectId) })
      // El brief archivado es un documento y el contacto es de la empresa: las
      // dos listas cambiaron.
      void client.invalidateQueries({ queryKey: qk.documents.all })
      void client.invalidateQueries({ queryKey: qk.companies.all })
      toast.success(t('onboarding.accepted'))
    },
    onError: (error) => {
      toast.error(errorMessage(error, t('onboarding.error.aceptar')))
    },
  })
}

export function useRevocarInvitacion(projectId: Uuid) {
  const client = useQueryClient()
  const t = useT()

  return useMutation({
    mutationFn: (id: Uuid) => api.delete<void>(`onboarding/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.projects.onboarding(projectId) })
      toast.success(t('onboarding.revoked'))
    },
    onError: (error) => {
      toast.error(errorMessage(error, t('onboarding.error.revocar')))
    },
  })
}
